import React, { useContext, useMemo, useState } from "react";

import {
  PanelHeader,
  Button,
  Divider,
  Tabs,
  Drawer,
} from "@pushpress/shared-components";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import {
  ClassForm,
  parseCalendarFormToPatchClassTemplateMutationInputValue,
  parseCalendarFormToUpdateClassTemplateMutationInputValue,
  parseClassFormDefaultValues,
  parseClassFormToMutationInputValue,
} from "./../CalendarForm.utils";
import {
  ClassItemForEditFragment,
  useGetCalendarItemForEditQuery,
  usePatchCalendarTemplateMutation,
  useUpdateCalendarItemMutation,
  useUpdateCalendarTemplateMutation,
} from "../CalendarForm-generated.hooks";
import { useToggle } from "../../../../hooks/state";
import { AppContext } from "../../../../context/app";
import { getWeekDay } from "../../../../utils/dateUtils";
import { defaultToastOptions, extractError } from "../../../../utils/misc";

import type { CalendarItemConfirmationScope } from "./../ConfirmationModal";

import ClassDetails from "./ClassDetails";
import CalendarItemConfirmationModal from "./../ConfirmationModal";
import ClassSettings from "./ClassSettings";

type EditClassDrawerProps = {
  visible: boolean;
  calendarItemUuid?: string;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
};

const EditClassDrawer = (props: EditClassDrawerProps) => {
  return (
    <Drawer
      data-cy="edit-drawer"
      visible={props.visible}
      anchor="right"
      width="400px"
    >
      {props.visible && <EditClassHydrator {...props} />}
    </Drawer>
  );
};

const EditClassHydrator = (props: EditClassDrawerProps) => {
  const { calendarItemUuid } = props;
  const { client } = useContext(AppContext);
  const { data } = useGetCalendarItemForEditQuery({
    variables: {
      getCalendarItemInput: {
        uuid: calendarItemUuid || "",
        clientUuid: client.client_uuid || client.uuid,
      },
    },
    skip: !calendarItemUuid,
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
  });

  const defaultValues = useMemo<Partial<ClassForm> | undefined>(() => {
    if (!data?.getCalendarItem) return undefined;

    return parseClassFormDefaultValues(data?.getCalendarItem);
  }, [data?.getCalendarItem]);

  if (!calendarItemUuid) return null;

  return (
    <>
      {defaultValues && (
        <EditClass
          {...props}
          calendarItemUuid={calendarItemUuid}
          defaultValues={defaultValues}
          templateId={data?.getCalendarItem.templateId || ""}
          classItem={data?.getCalendarItem}
        />
      )}
    </>
  );
};

type EditClassProps = {
  calendarItemUuid: string;
  defaultValues: Partial<ClassForm>;
  templateId: string;
  visible: boolean;
  classItem?: ClassItemForEditFragment;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
};

const EditClass = ({
  onClose,
  defaultValues,
  calendarItemUuid,
  templateId,
  classItem,
  onSuccess,
}: EditClassProps) => {
  const { control, handleSubmit, trigger } = useForm<ClassForm>({
    defaultValues,
    mode: "onTouched",
  });
  const [selectingSaveScope, selectingSaveScopeActions] = useToggle(false);
  const [savingUpdate, savingUpdateActions] = useToggle();
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);

  const [updateCalendarItemTemplateMutation] =
    useUpdateCalendarTemplateMutation();
  const [patchCalendarItemMutation] = usePatchCalendarTemplateMutation();
  const [updateCalendarItemMutation] = useUpdateCalendarItemMutation();

  const startDate = defaultValues?.date?.start;
  if (!startDate) {
    toast.error(
      "An unexpected empty start date occurred, please reload this page",
      defaultToastOptions,
    );
    return <></>;
  }

  const save = (values: ClassForm) => {
    const isAttachedToATemplate = !(Number(templateId) === 0);
    if (isAttachedToATemplate) {
      selectingSaveScopeActions.on();
    } else {
      onSaveScopeSelected("this", values);
    }
  };

  const handleScopeSelected = (scope: CalendarItemConfirmationScope) => {
    handleSubmit((values: ClassForm) => {
      onSaveScopeSelected(scope, values);
    })();
  };

  const onSaveScopeSelected = async (
    saveScope: CalendarItemConfirmationScope,
    values: ClassForm,
  ) => {
    if (!values) {
      toast.error(
        "An unexpected error occurred, please reload this page",
        defaultToastOptions,
      );
      return;
    }

    try {
      savingUpdateActions.on();
      switch (saveScope) {
        case "this":
          await updateCalendarItemMutation({
            variables: {
              updateCalendarItemInput: parseClassFormToMutationInputValue(
                calendarItemUuid,
                values,
              ),
            },
          });
          break;
        case "all":
          await updateCalendarItemTemplateMutation({
            variables: {
              updateCalendarTemplateItemInput:
                parseCalendarFormToUpdateClassTemplateMutationInputValue({
                  values: values,
                  templateId,
                  days: undefined,
                }),
            },
          });
          break;
        case "all of weekday":
          await patchCalendarItemMutation({
            variables: {
              patchCalendarTemplateItemInput:
                parseCalendarFormToPatchClassTemplateMutationInputValue({
                  values: values,
                  templateId,
                  days: [getWeekDay(startDate)],
                }),
            },
          });
          break;
        default:
          savingUpdateActions.off();
          return;
      }
      toast.success(
        `Success! Your class has been updated!`,
        defaultToastOptions,
      );
      onSuccess();
    } catch (err) {
      toast.error(extractError(err), defaultToastOptions);
    } finally {
      savingUpdateActions.off();
    }
  };

  return (
    <>
      <CalendarItemConfirmationModal
        classItem={classItem}
        title={"Edit class"}
        loading={savingUpdate}
        visible={selectingSaveScope}
        onClose={selectingSaveScopeActions.off}
        onScopeSelected={handleScopeSelected}
      />
      <PanelHeader title="Edit class">
        <PanelHeader.ActionMenu>
          <Button
            text="Cancel"
            buttonType="secondary"
            size="medium"
            onClick={onClose}
          />
          <Button
            text="Save"
            buttonType="primary"
            loading={savingUpdate}
            size="medium"
            iconLocation="left"
            onClick={handleSubmit(save)}
          />
        </PanelHeader.ActionMenu>
      </PanelHeader>
      <Divider />
      <Tabs
        tabHeaders={["Details", "Settings"]}
        selectedIndex={selectedTabIndex}
        changeTab={setSelectedTabIndex}
        onTabClick={setSelectedTabIndex}
        align="left"
        hideWithCss
        onTabFocus={setSelectedTabIndex}
      >
        <ClassDetails
          control={control}
          editMode
          trigger={trigger}
          initialCalendarItemTypeUuid={defaultValues?.type?.uuid}
        />
        <ClassSettings control={control} />
      </Tabs>
    </>
  );
};

export default EditClassDrawer;
